import { useState } from 'react';
import { previewEmailAudience, resolveEmailAudience } from '../../../api/adminEmailCenter';
import EmailCenterPage, { EmptyState, PageSection, SelectField, TextField, TextAreaField } from './EmailCenterShared';

const defaultForm = {
  audienceType: 'custom_filter',
  audienceMeta: {
    segment: 'all_users',
    city: '',
    state: '',
    minBookings: '',
    createdFrom: '',
    createdTo: '',
    manualEmails: '',
  },
};

export default function EmailAudience() {
  const [form, setForm] = useState(defaultForm);
  const [state, setState] = useState({ loading: false, error: '', preview: null, resolved: null });

  const run = async (mode) => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      if (mode === 'preview') {
        const data = await previewEmailAudience(form);
        setState({ loading: false, error: '', preview: data.preview, resolved: null });
      } else {
        const data = await resolveEmailAudience(form);
        setState({ loading: false, error: '', preview: null, resolved: data });
      }
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message || 'Audience lookup failed' }));
    }
  };

  const recipients = state.preview?.sampleRecipients || state.resolved?.recipients || [];

  return (
    <EmailCenterPage
      title="Audience Explorer"
      description="Preview segmentation logic before campaign creation and inspect sample users, invalid emails, and promotional suppressions."
    >
      {state.error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div> : null}

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <PageSection title="Segmentation Rules" subtitle="Use the same audience engine the campaign builder relies on.">
          <div className="grid gap-4 md:grid-cols-2">
            <SelectField label="Audience Mode" value={form.audienceType} onChange={(e) => setForm((current) => ({ ...current, audienceType: e.target.value }))}>
              <option value="custom_filter">Segmented Audience</option>
              <option value="manual_selection">Manual Email List</option>
            </SelectField>
            {form.audienceType === 'custom_filter' ? (
              <SelectField
                label="Segment"
                value={form.audienceMeta.segment}
                onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, segment: e.target.value } }))}
              >
                <option value="all_users">All active users</option>
                <option value="customers">Only customers</option>
                <option value="hosts">Only hosts</option>
                <option value="no_booking_yet">Users with no booking yet</option>
                <option value="top_customers">Top customers</option>
              </SelectField>
            ) : null}
            {form.audienceType === 'custom_filter' ? (
              <>
                <TextField label="City" value={form.audienceMeta.city} onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, city: e.target.value } }))} />
                <TextField label="State" value={form.audienceMeta.state} onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, state: e.target.value } }))} />
                <TextField label="Min Bookings" type="number" value={form.audienceMeta.minBookings} onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, minBookings: e.target.value } }))} />
                <TextField label="Created From" type="date" value={form.audienceMeta.createdFrom} onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, createdFrom: e.target.value } }))} />
                <TextField label="Created To" type="date" value={form.audienceMeta.createdTo} onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, createdTo: e.target.value } }))} />
              </>
            ) : (
              <div className="md:col-span-2">
                <TextAreaField
                  label="Manual Emails"
                  placeholder="one@example.com&#10;two@example.com"
                  value={form.audienceMeta.manualEmails}
                  onChange={(e) => setForm((current) => ({ ...current, audienceMeta: { ...current.audienceMeta, manualEmails: e.target.value } }))}
                />
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={() => run('preview')} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
              {state.loading ? 'Working...' : 'Preview Count'}
            </button>
            <button type="button" onClick={() => run('resolve')} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
              Resolve Full Audience
            </button>
          </div>
        </PageSection>

        <PageSection title="Audience Output" subtitle="Recipient count, invalid emails, promotional suppressions, and sample contacts.">
          {!state.preview && !state.resolved ? (
            <EmptyState title="No audience evaluated yet" body="Run a preview or resolve action to inspect the current segment result." />
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-[24px] border border-slate-200 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Recipients</p>
                  <div className="mt-4 text-3xl font-semibold text-slate-900">{state.preview?.totalRecipients || state.resolved?.totalRecipients || 0}</div>
                </div>
                <div className="rounded-[24px] border border-slate-200 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Invalid</p>
                  <div className="mt-4 text-3xl font-semibold text-slate-900">{state.preview?.invalidEmails?.length || state.resolved?.invalidEmails?.length || 0}</div>
                </div>
                <div className="rounded-[24px] border border-slate-200 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Suppressed</p>
                  <div className="mt-4 text-3xl font-semibold text-slate-900">{state.preview?.skipped?.length || state.resolved?.skipped?.length || 0}</div>
                </div>
              </div>
              <div className="space-y-3">
                {recipients.map((item) => (
                  <div key={item.email} className="rounded-[24px] border border-slate-100 bg-slate-50/60 p-4">
                    <p className="font-semibold text-slate-900">{item.fullName || 'Guest'}</p>
                    <p className="mt-1 text-sm text-slate-500">{item.email}</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-400">{item.metadata?.entityType || item.sourceType}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </PageSection>
      </div>
    </EmailCenterPage>
  );
}
